import { CartAddEvent } from '@theme/events';
import { resolveOffer, subscribeOffer, syncCart } from '@theme/cart-special-offer';

const AUTO_HIDE_MS = 5000;

/* A claim card needs time to pick a size, so it holds longer, and any touch
 * inside it cancels the clock altogether. */
const CLAIM_HIDE_MS = 12000;

/* CartAddEvent.eventName IS ThemeEvents.cartUpdate — adds, removals and
 * quantity changes all arrive on 'cart:update'. `source` is the only thing in
 * the payload that separates them, so the drop only answers to the sources that
 * genuinely add a line. */
const ADD_SOURCES = new Set(['product-form-component', 'quick-order-quantity']);

/* Liquid resolves plurals server-side, so both forms are rendered once with a
 * sentinel count and the client swaps the real number in. */
const PLURAL_SENTINEL = '999';

/** @param {string | undefined} url @param {number} size */
function sized(url, size) {
  return url ? url.replace(/(\.[a-z]+)(\?|$)/i, `_${size}x${size}$1$2`) : '';
}

/**
 * Buck Mason style "just added" confirmation.
 *
 * Listens for the same CartAddEvent that Horizon's drawer auto-open uses, then
 * slides a summary down from the top instead of opening the whole drawer. The
 * event carries a variantId but not the line item, so the matching line is read
 * back from /cart.js — one small request, and only after an add.
 *
 * While the free gift can still be claimed, the drop leads with the same claim
 * card the drawer shows (size choice and Add button included); once the hat
 * lands it is confirmed like any other line. Whether the gift leads is
 * `resolveOffer()`'s call, never this file's.
 */
class CartAddedDrop extends HTMLElement {
  /** @type {number | undefined} */
  #hideTimer;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  /** @type {number} */
  #lastItemCount = Number.NaN;

  /** True from an add event arriving until the drop has re-rendered for it. */
  #adding = false;

  connectedCallback() {
    this.#lastItemCount = Number(this.dataset.itemCount);
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, { signal });
    this.querySelector('[data-close]')?.addEventListener('click', this.close, { signal });
    this.querySelector('[data-view-cart]')?.addEventListener('click', this.#viewCart, { signal });
    this.addEventListener('mouseenter', this.#cancelAutoHide, { signal });

    const giftBody = this.querySelector('[data-gift-body]');
    for (const type of ['pointerdown', 'focusin', 'change']) {
      giftBody?.addEventListener(type, this.#cancelAutoHide, { signal });
    }

    this.#unsubscribe = subscribeOffer(this.#onOfferChange);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    clearTimeout(this.#hideTimer);
  }

  #handleCartAdd = async (/** @type {any} */ event) => {
    const data = event?.detail?.data;
    if (data?.didError) return;
    if (!ADD_SOURCES.has(data?.source)) {
      // A removal or quantity change. Keep the count in step and stay quiet.
      this.#lastItemCount = Number(event?.detail?.resource?.item_count ?? this.#lastItemCount);
      return;
    }

    this.#adding = true;
    try {
      // The gift card, the PDP form and the shelf's quick-add all dispatch from
      // product-form-component, so whichever of them landed the line is confirmed.
      const found = await this.#findItem(data?.variantId);
      if (!found) return;
      const { item, cart } = found;

      // The offer store reads the same cart, so the gift decision below is
      // made against the cart this add produced, not the one before it.
      syncCart(cart.items);

      /* Second guard: quick-order-quantity fires on decrements too, so the bag
       * has to have actually grown. */
      const count = Number(cart.item_count);
      const grew = !Number.isFinite(this.#lastItemCount) || count > this.#lastItemCount;
      this.#lastItemCount = count;
      if (!grew) return;

      const leadWithGift = this.#render(item, cart);
      this.open(leadWithGift ? CLAIM_HIDE_MS : AUTO_HIDE_MS);
    } finally {
      this.#adding = false;
    }
  };

  /**
   * @param {string | number | undefined} variantId
   * @returns {Promise<{ item: any, cart: any } | null>}
   */
  async #findItem(variantId) {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;

      const cart = await response.json();
      const items = cart?.items ?? [];
      if (!items.length) return null;

      const match = variantId ? items.find((line) => String(line.variant_id) === String(variantId)) : null;
      return { item: match ?? items[0], cart };
    } catch (_error) {
      // A failed read is not worth interrupting the shopper over.
      return null;
    }
  }

  /**
   * The offer changed underneath an open claim card (the window lapsed, or the
   * hat landed from somewhere else). The card inside has already hidden
   * itself; the drop has nothing left to say, so it leaves. An add in flight
   * re-renders on its own.
   */
  #onOfferChange = () => {
    if (this.#adding || this.dataset.open !== 'true') return;
    const giftBody = /** @type {HTMLElement | null} */ (this.querySelector('[data-gift-body]'));
    if (!giftBody || giftBody.hidden) return;
    if (resolveOffer().state === 'claim') return;
    this.close();
  };

  /**
   * @param {any} item
   * @param {any} cart
   * @returns {boolean} whether the gift claim leads
   */
  #render(item, cart) {
    const headline = this.querySelector('[data-headline]');
    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-image]'));
    const title = this.querySelector('[data-title]');
    const meta = this.querySelector('[data-meta]');
    const giftBody = /** @type {HTMLElement | null} */ (this.querySelector('[data-gift-body]'));
    const itemBody = /** @type {HTMLElement | null} */ (this.querySelector('[data-item-body]'));
    const confirm = /** @type {HTMLElement | null} */ (this.querySelector('[data-confirm]'));
    const confirmText = this.querySelector('[data-confirm-text]');
    const confirmImage = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-confirm-image]'));

    /* Adding the gift itself is not news about the gift, so that case falls
     * back to the ordinary confirmation. */
    const isGiftItem = String(item.product_id) === String(this.dataset.giftProductId);
    const leadWithGift = Boolean(giftBody) && resolveOffer().state === 'claim' && !isGiftItem;

    if (giftBody) giftBody.hidden = !leadWithGift;
    if (itemBody) itemBody.hidden = leadWithGift;

    if (confirm && confirmText) {
      const name = item.product_title || item.title || '';
      const line = leadWithGift && name ? [name, this.#countLabel(cart.item_count), this.#money(cart.total_price)] : [];
      confirmText.textContent = line.filter(Boolean).join(' · ');
      confirm.hidden = !confirmText.textContent;

      if (confirmImage) {
        const src = sized(item.featured_image?.url || item.image, 120);
        if (src) {
          confirmImage.src = src;
          confirmImage.alt = item.featured_image?.alt || name;
        } else {
          confirmImage.removeAttribute('src');
        }
      }
    }

    if (headline) {
      const giftHeadline = this.dataset.headlineGift;
      headline.textContent =
        (leadWithGift && giftHeadline) || this.dataset.headlineDefault || headline.textContent;
    }

    if (leadWithGift) return true;

    if (image) {
      const source = sized(item.featured_image?.url || item.image, 440);
      if (source) {
        image.src = source;
        image.alt = item.featured_image?.alt || item.product_title || '';
      }
    }

    if (title) title.textContent = item.product_title || item.title || '';

    if (meta) {
      // variant_title is null on single-variant products, which is exactly the
      // case where there is nothing worth repeating back. The gift line says
      // Free, because that is the news.
      const options = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const parts = [options, isGiftItem ? this.dataset.freeLabel : ''].filter(Boolean);
      meta.textContent = parts.join(' · ');
      meta.hidden = !parts.length;
    }

    return false;
  }

  /** @param {number} count */
  #countLabel(count) {
    const n = Number(count);
    if (!Number.isFinite(n)) return '';
    const template = n === 1 ? this.dataset.countOne : this.dataset.countOther;
    return template ? template.replace(PLURAL_SENTINEL, String(n)) : '';
  }

  /** @param {number} cents */
  #money(cents) {
    if (!Number.isFinite(Number(cents))) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(Number(cents) / 100);
    } catch (_error) {
      return '';
    }
  }

  /** @param {number} [hideAfter] */
  open(hideAfter = AUTO_HIDE_MS) {
    clearTimeout(this.#hideTimer);
    this.hidden = false;

    // Two frames: one to let `hidden` clear, one to let the browser record the
    // off-screen transform as the transition's start value.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dataset.open = 'true';
      });
    });

    this.#hideTimer = setTimeout(this.close, hideAfter);
  }

  close = () => {
    clearTimeout(this.#hideTimer);
    if (this.dataset.open !== 'true') {
      this.hidden = true;
      return;
    }

    delete this.dataset.open;

    const finish = () => {
      this.hidden = true;
    };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      finish();
      return;
    }
    this.addEventListener('transitionend', finish, { once: true });
    // Backstop in case the transition never fires (background tab, display:none).
    setTimeout(finish, 500);
  };

  #cancelAutoHide = () => {
    clearTimeout(this.#hideTimer);
  };

  #viewCart = () => {
    this.close();

    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    window.location.href = `${Shopify.routes.root}cart`;
  };
}

if (!customElements.get('cart-added-drop')) {
  customElements.define('cart-added-drop', CartAddedDrop);
}
